// frontend/src/pages/GoldReservePage.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../services/api';
import ReactECharts from 'echarts-for-react';
import Loading from '../../components/Loading';
import { createChartOptions } from '../../utils/chartOptions'; 
import { Typography, Card, Row, Tabs, Radio, Space, Segmented, Divider } from 'antd'; 
import { GoldOutlined, BankOutlined, FundOutlined, LineChartOutlined } from '@ant-design/icons';
import ResponsiveTypography from '../../components/ResponsiveTypography';

const { Title } = ResponsiveTypography;
const { Paragraph } = Typography;
const { TabPane } = Tabs;

// --- Constants & Helpers ---
const DISTINCT_PALETTE = [
    '#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F', '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC',
    '#86BCB6', '#E19D29', '#A0CBE8', '#FFBE7D', '#8CD17D', '#D37295', '#FABFD2', '#D4A6C8', '#9D7660', '#D7B5A6',
    '#393b79', '#5254a3', '#6b6ecf', '#9c9ede', '#637939', '#8ca252', '#b5cf6b', '#cedb9c', '#8c6d31', '#bd9e39',
    '#e7ba52', '#e7cb94', '#843c39', '#ad494a', '#d6616b', '#e7969c', '#7b4173', '#a55194', '#ce6dbd', '#de9ed6',
    '#3182bd', '#6baed6', '#9ecae1', '#c6dbef', '#e6550d', '#fd8d3c', '#fdae6b', '#fdd0a2', '#31a354', '#74c476'
];
const OTHERS_COLOR = '#95A5A6'; 
const GOLD_LINE_COLOR = '#f39c12'; 
const NET_LINE_COLOR = '#2980b9'; 
const ETF_FLOW_COLOR = '#8e44ad';
const CB_CHANGE_COLOR = '#27ae60';

const mergeData = (mainList, priceList) => {
    const dataMap = new Map();
    mainList.forEach(item => dataMap.set(item.Date, { ...item }));
    priceList.forEach(item => {
        const date = item.Date;
        const existing = dataMap.get(date) || { Date: date, TOTAL: null };
        dataMap.set(date, { ...existing, Gold_Price: item.Gold_Price });
    });
    const mergedArray = Array.from(dataMap.values()).sort((a, b) => new Date(a.Date) - new Date(b.Date));
    
    if (mainList.length > 0) {
        const firstMainDate = mainList[0].Date;
        return mergedArray.filter(item => item.Date >= firstMainDate);
    }
    return mergedArray;
};

// [수정됨] 소수점 없이(Fixed 0) 세 자리 콤마 포맷 적용
const formatValue = (val) => {
    if (val === 0 || val === null || val === undefined) return 'N/A';
    // 반올림 후 로케일 문자열(콤마 추가) 변환
    return Math.round(val).toLocaleString();
};

const axisDateFormatter = (value) => {
    const date = new Date(value);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const GoldReservePage = () => {
    const [dataSource, setDataSource] = useState('combined'); // 'cb' | 'etf' | 'combined'
    const [chartData, setChartData] = useState([]);
    const [comparisonData, setComparisonData] = useState([]); 
    const [countryList, setCountryList] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [freq, setFreq] = useState('QE'); 
    const [mode, setMode] = useState('holdings'); 
    
    // 모드 자동 전환 Effect
    useEffect(() => {
        if (dataSource === 'combined') {
            setMode('composite');
        } else {
            setMode('holdings'); 
        }
    }, [dataSource]);

    const colorMap = useMemo(() => {
        if (!countryList || countryList.length === 0) return {};
        const newColorMap = {};
        const actualCountries = countryList.filter(c => c !== 'Others').sort();
        actualCountries.forEach((country, index) => {
            newColorMap[country] = DISTINCT_PALETTE[index % DISTINCT_PALETTE.length];
        });
        newColorMap['Others'] = OTHERS_COLOR;
        return newColorMap;
    }, [countryList]);

    // 메인 데이터 Fetching
    useEffect(() => {
        if (dataSource === 'combined' || mode === 'composite') return;

        setLoading(true);
        const fetchData = async () => {
            try {
                let mainEndpoint = '';
                if (dataSource === 'cb') {
                    if (mode === 'holdings') mainEndpoint = '/gold/reserves-volume';
                    else if (mode === 'net_change') mainEndpoint = '/gold/reserves-change';
                    else if (mode === 'value') mainEndpoint = '/gold/purchase-value';
                } else {
                    if (mode === 'holdings') mainEndpoint = '/gold/etf-holdings';
                    else if (mode === 'net_change') mainEndpoint = '/gold/etf-flows';
                }

                if (mainEndpoint) {
                    const [mainRes, priceRes] = await Promise.all([
                        apiClient.get(mainEndpoint, { params: { freq } }),
                        apiClient.get('/gold/price', { params: { freq } })
                    ]);

                    setChartData(mergeData(mainRes.data.data || [], priceRes.data.data || []));
                    setCountryList(mainRes.data.countries || []); 
                }
            } catch (error) {
                console.error("Failed to fetch data:", error);
                setChartData([]);
                setCountryList([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [freq, mode, dataSource]);

    // Comparison (Combined) 데이터 Fetching
    useEffect(() => {
        if (dataSource !== 'combined' && mode !== 'composite') return;

        setLoading(true);
        const fetchComparison = async () => {
            try {
                const [cbRes, etfRes, priceRes] = await Promise.all([
                    apiClient.get('/gold/reserves-change', { params: { freq } }),
                    apiClient.get('/gold/etf-flows', { params: { freq } }),
                    apiClient.get('/gold/price', { params: { freq } })
                ]);

                const cbData = cbRes.data.data || [];
                const etfData = etfRes.data.data || [];
                const priceData = priceRes.data.data || [];

                const cbStartDate = cbData.length > 0 ? cbData[0].Date : '0000-00-00';
                const etfStartDate = etfData.length > 0 ? etfData[0].Date : '0000-00-00';
                const intersectionStart = cbStartDate > etfStartDate ? cbStartDate : etfStartDate;

                const dataMap = new Map();

                priceData.forEach(item => {
                    if (item.Date >= intersectionStart) {
                        dataMap.set(item.Date, { 
                            Date: item.Date, Gold_Price: item.Gold_Price, CB_Net: null, ETF_Flow: null, Total_Net: null
                        });
                    }
                });

                cbData.forEach(item => {
                    if (item.Date >= intersectionStart) {
                        const existing = dataMap.get(item.Date) || { Date: item.Date, Gold_Price: null, CB_Net: null, ETF_Flow: null, Total_Net: null };
                        existing.CB_Net = item.TOTAL;
                        dataMap.set(item.Date, existing);
                    }
                });

                etfData.forEach(item => {
                    if (item.Date >= intersectionStart) {
                        const existing = dataMap.get(item.Date) || { Date: item.Date, Gold_Price: null, CB_Net: null, ETF_Flow: null, Total_Net: null };
                        existing.ETF_Flow = item.TOTAL;
                        dataMap.set(item.Date, existing);
                    }
                });

                const mergedArray = Array.from(dataMap.values()).sort((a, b) => new Date(a.Date) - new Date(b.Date));
                let cumTotal = 0, cumCB = 0, cumETF = 0;

                const enrichedArray = mergedArray.map(item => {
                    const cbVal = item.CB_Net || 0;
                    const etfVal = item.ETF_Flow || 0;
                    
                    if (item.CB_Net !== null || item.ETF_Flow !== null) {
                        item.Total_Net = cbVal + etfVal;
                        cumTotal += (cbVal + etfVal);
                        cumCB += cbVal;
                        cumETF += etfVal;
                    }

                    return { 
                        ...item, 
                        Cumulative_Total_Net: cumTotal,
                        Cumulative_CB: cumCB,
                        Cumulative_ETF: cumETF
                    };
                });

                setComparisonData(enrichedArray);
            } catch (error) {
                console.error("Failed to fetch comparison data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchComparison();
    }, [freq, mode, dataSource]);

    // Breakdown Chart
    const getBreakdownChartOption = useCallback(() => {
        if (!chartData || chartData.length === 0) return {};

        const sortedCountryList = [...countryList];
        const othersIndex = sortedCountryList.indexOf('Others');
        if (othersIndex > -1) {
            sortedCountryList.splice(othersIndex, 1);
            sortedCountryList.push('Others');
        }

        const series = sortedCountryList.map((country) => ({
            name: country,
            type: 'bar',
            stack: 'total',
            barMaxWidth: 60,
            itemStyle: { 
                color: colorMap[country] || OTHERS_COLOR,
                borderColor: 'rgba(255,255,255,0.2)', borderWidth: 0.5
            },
            data: chartData.map(item => item[country])
        }));

        const isEtf = dataSource === 'etf';
        const lineName = (mode === 'holdings') 
            ? (isEtf ? 'TOTAL' : 'TOTAL') 
            : (isEtf ? 'Net Flows' : 'Net Change');

        series.push({
            name: lineName,
            type: 'line',
            data: chartData.map(d => d.TOTAL),
            smooth: false, symbol: 'circle', symbolSize: 6, connectNulls: true,
            lineStyle: { width: 2, color: NET_LINE_COLOR },
            itemStyle: { color: '#ffffff', borderColor: NET_LINE_COLOR, borderWidth: 2 },
            z: 10
        });

        return createChartOptions({
            legend: { show: false }, 
            tooltip: { 
                trigger: 'axis',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                formatter: (params) => {
                    let tooltip = `<div style="font-weight:bold;margin-bottom:5px;">${params[0].axisValueLabel}</div>`;
                    
                    const lineSeries = params.find(p => p.seriesName === lineName);
                    if (lineSeries) {
                        const val = formatValue(lineSeries.value);
                        if (val !== 'N/A') {
                            tooltip += `<div style="display:flex; justify-content:space-between; gap: 1rem; margin-bottom:8px; border-bottom:1px solid #eee; padding-bottom:5px;">
                                <span style="font-weight:bold; color:${NET_LINE_COLOR}">● ${lineSeries.seriesName}</span>
                                <span style="font-weight:bold;">${val}</span>
                            </div>`;
                        }
                    }

                    const bars = params.filter(p => p.seriesType === 'bar' && p.value !== null && Math.abs(p.value) > 0.001);
                    bars.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
                    
                    bars.forEach(item => {
                        tooltip += `<div style="display:flex; justify-content:space-between; font-size:0.9em;">
                            <span style="color:#555"><span style="display:inline-block;margin-right:6px;width:8px;height:8px;background-color:${item.color};border-radius:50%;"></span>${item.seriesName}</span>
                            <b>${formatValue(item.value)}</b>
                        </div>`;
                    });
                    return tooltip;
                }
            },
            grid: { left: '5%', right: '4%', bottom: '8%', top: '15%', containLabel: true },
            xAxis: { 
                type: 'category', 
                data: chartData.map(d => d.Date),
                axisLabel: { color: '#7f8c8d', formatter: axisDateFormatter }
            },
            yAxis: [{ type: 'value', name: 'Tonnes', scale: mode === 'holdings' }],
            dataZoom: [{ type: 'slider', show: true, bottom: '2%', height: '5%' }],
            series: series
        });
    }, [chartData, countryList, colorMap, mode, dataSource]);
    
    // Value Chart
    const getValueChartOption = useCallback(() => {
        if (!chartData || chartData.length === 0) return {};

        return createChartOptions({
            legend: { show: true, top: '0%' }, 
            tooltip: { 
                trigger: 'axis',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                formatter: (params) => {
                    let html = `<div style="font-weight:bold;margin-bottom:5px;">${params[0].axisValueLabel}</div>`;
                    params.forEach(p => {
                        // [수정됨] formatValue의 2번째 인자 제거 (기본값: 정수+콤마)
                        const displayVal = formatValue(p.value);
                        if (displayVal !== 'N/A' || p.seriesName.includes('Price')) {
                            const unit = p.seriesName.includes('Price') ? '$' : '$B';
                            const markerColor = p.seriesName.includes('Price') ? GOLD_LINE_COLOR : p.color;
                            const valStr = unit === '$' ? '$' + displayVal : displayVal + 'B';
                            html += `<div><span style="color:${markerColor}">●</span> ${p.seriesName}: <b>${valStr}</b></div>`;
                        }
                    });
                    return html;
                }
            },
            grid: { left: '5%', right: '4%', bottom: '8%', top: '15%', containLabel: true },
            xAxis: { 
                type: 'category', 
                data: chartData.map(d => d.Date),
                axisLabel: { color: '#7f8c8d', formatter: axisDateFormatter }
            },
            yAxis: [
                { type: 'value', name: 'Value ($B)', splitLine: { lineStyle: { type: 'dashed', color: '#eee' } } },
                { type: 'log', name: 'Price ($/oz)', scale: true, logBase: 10, splitLine: { show: false }, axisLabel: { color: GOLD_LINE_COLOR } }
            ],
            dataZoom: [{ type: 'slider', show: true, bottom: '2%', height: '5%' }],
            series: [
                {
                    name: 'Net Purchase Value ($B)',
                    type: 'bar',
                    data: chartData.map(d => d.PurchaseValueB),
                    itemStyle: { color: (params) => params.value >= 0 ? '#3498db' : '#e74c3c' },
                    yAxisIndex: 0
                },
                {
                    name: 'Gold Price ($/oz)',
                    type: 'line',
                    data: chartData.map(d => d.Gold_Price),
                    symbol: 'none', smooth: false, 
                    lineStyle: { width: 2, color: GOLD_LINE_COLOR, type: 'solid' },
                    itemStyle: { color: GOLD_LINE_COLOR },
                    yAxisIndex: 1
                }
            ]
        });
    }, [chartData]);

    // Cumulative Chart
    const getCumulativeChartOption = useCallback(() => {
        if (!comparisonData || comparisonData.length === 0) return {};

        return {
            legend: { show: true, top: '0%', left: 'center' }, 
            tooltip: { 
                trigger: 'axis',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                formatter: (params) => {
                    let html = `<div style="font-weight:bold;margin-bottom:5px;">${params[0].axisValueLabel}</div>`;
                    params.forEach(p => {
                        if (p.seriesName.includes('Total') || p.seriesName.includes('Price') || p.seriesType === 'bar') {
                             // [수정됨] formatValue 사용 (정수+콤마)
                             html += `<div><span style="color:${p.color}">●</span> ${p.seriesName}: <b>${formatValue(p.value)}</b></div>`;
                        }
                    });
                    return html;
                }
            },
            grid: { left: '5%', right: '4%', bottom: '8%', top: '15%', containLabel: true },
            xAxis: { 
                type: 'category', 
                data: comparisonData.map(d => d.Date),
                axisLabel: { color: '#7f8c8d' }
            },
            yAxis: [
                { type: 'value', name: 'Tonnes', scale: false, splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } } },
                { type: 'log', name: 'Price ($/oz)', logBase: 10, scale: true, axisLabel: { color: GOLD_LINE_COLOR }, splitLine: { show: false } }
            ],
            dataZoom: [{ type: 'slider', show: true, bottom: '2%', height: '5%' }],
            series: [
                {
                    name: '누적 ETF 순유입',
                    type: 'bar', stack: 'total',
                    data: comparisonData.map(d => d.Cumulative_ETF),
                    itemStyle: { color: ETF_FLOW_COLOR }, yAxisIndex: 0
                },
                {
                    name: '누적 중앙은행 순매입',
                    type: 'bar', stack: 'total',
                    data: comparisonData.map(d => d.Cumulative_CB),
                    itemStyle: { color: CB_CHANGE_COLOR }, yAxisIndex: 0
                },
                {
                    name: '누적 변화량 합계',
                    type: 'line',
                    data: comparisonData.map(d => d.Cumulative_Total_Net),
                    showSymbol: false, smooth: false, 
                    lineStyle: { width: 3, color: NET_LINE_COLOR }, yAxisIndex: 0
                },
                {
                    name: 'Gold Price ($/oz)',
                    type: 'line',
                    data: comparisonData.map(d => d.Gold_Price),
                    showSymbol: false, smooth: false, 
                    lineStyle: { width: 2, color: GOLD_LINE_COLOR },
                    itemStyle: { color: GOLD_LINE_COLOR }, yAxisIndex: 1
                }
            ]
        };
    }, [comparisonData]);

    // Comparison Chart
    const getComparisonChartOption = useCallback(() => {
        if (!comparisonData || comparisonData.length === 0) return {};

        return {
            legend: { show: true, top: '0%', left: 'center', itemGap: 20 }, 
            tooltip: { 
                trigger: 'axis',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                formatter: (params) => {
                    let html = `<div style="font-weight:bold;margin-bottom:5px;border-bottom:1px solid #eee;padding-bottom:5px;">${params[0].axisValueLabel}</div>`;
                    
                    const totalLine = params.find(p => p.seriesName === 'Aggregate Net Change');
                    if (totalLine) {
                        const val = formatValue(totalLine.value);
                        if (val !== 'N/A') {
                             html += `<div style="display:flex; justify-content:space-between; margin-bottom:8px; border-bottom:1px solid #eee; padding-bottom:5px;">
                                <span style="font-weight:bold; color:${NET_LINE_COLOR}">● Aggregate Net Change</span>
                                <span style="font-weight:bold;">${val}</span>
                            </div>`;
                        }
                    }

                    params.forEach(p => {
                        if (p.seriesName === 'Aggregate Net Change') return;
                        const displayVal = formatValue(p.value);
                        if (displayVal !== 'N/A') {
                            html += `<div style="display:flex; justify-content:space-between; align-items:center; gap:15px;">
                                <span><span style="color:${p.color}">●</span> ${p.seriesName}</span>
                                <span style="font-weight:bold;">${displayVal}</span>
                            </div>`;
                        }
                    });
                    return html;
                }
            },
            grid: { left: '5%', right: '4%', bottom: '8%', top: '15%', containLabel: true },
            xAxis: { 
                type: 'category', 
                data: comparisonData.map(d => d.Date),
                axisLabel: { color: '#7f8c8d' }
            },
            yAxis: [
                { type: 'value', name: 'Tonnes', scale: false, splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } } }
            ],
            dataZoom: [{ type: 'slider', show: true, bottom: '2%', height: '5%' }],
            series: [
                {
                    name: '금 ETF순유입',
                    type: 'bar', stack: 'total', 
                    data: comparisonData.map(d => d.ETF_Flow),
                    itemStyle: { color: ETF_FLOW_COLOR }, yAxisIndex: 0
                },
                {
                    name: '중앙은행 순매입',
                    type: 'bar', stack: 'total', 
                    data: comparisonData.map(d => d.CB_Net),
                    itemStyle: { color: CB_CHANGE_COLOR }, yAxisIndex: 0
                },
                {
                    name: '합계', 
                    type: 'line',
                    data: comparisonData.map(d => d.Total_Net),
                    symbol: 'circle', symbolSize: 6, smooth: false, connectNulls: true,
                    lineStyle: { width: 2, color: NET_LINE_COLOR }, yAxisIndex: 0, z: 20 
                }
            ]
        };
    }, [comparisonData]);

    const handleTabChange = (key) => setMode(key);

    const getBreakdownTitle = () => {
        const isEtf = dataSource === 'etf';
        if (mode === 'holdings') {
            return isEtf ? 'Global ETF Holdings Trend (Region Breakdown)' : 'Official Gold Reserves (Country Breakdown)';
        }
        return isEtf ? 'Global ETF Net Flows (Region Breakdown)' : 'Central Bank Net Change (Country Breakdown)';
    };

    return (
        <div className="content-box">
            <div className="mb-8" style={{ marginBottom: '1rem' }} >
                <Title level={2} style={{ margin: '1rem 0.5rem', textAlign: 'left' }}>
                    <GoldOutlined style={{ marginRight: '10px', color: GOLD_LINE_COLOR }}/>
                    {dataSource === 'cb' ? '중앙은행 금 준비자산' 
                    : dataSource === 'etf' ? 'ETF 금 보유량 트렌드' 
                    : 'ETFs & 중앙은행 금 수요'}
                </Title>
                <Paragraph style={{ textAlign: 'left', margin: '0 1.0rem', wordBreak: 'break-all' }}>
                    본 페이지는 전 세계 중앙은행의 금 준비자산(Official Reserves)과 금 ETF자금 흐름(Fund Flows)을 분석하여 금 시장의 거시적 투자 수요를 시각화합니다.<br/> 
                    각 탭을 통해 부문별 보유 잔고(Holdings)와 순변동(Net Change), 그리고 금 가격과의 상관관계를 입체적으로 조망할 수 있습니다.
                </Paragraph>
            </div>

            <Card bodyStyle={{ padding: '1rem' }} style={{ margin: '1.5rem 0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Row justify='space-between' align="middle" style={{ gap: '20px', flexWrap: 'wrap' }}>
                    <Space direction="horizontal" align="center">
                        <Segmented
                            options={[
                                { label: '은행 & ETFs', value: 'combined'},
                                { label: '중앙은행', value: 'cb', icon: <BankOutlined /> },
                                { label: 'ETFs', value: 'etf', icon: <FundOutlined /> },
                            ]}
                            value={dataSource}
                            onChange={setDataSource}
                            size="medium"
                        />
                    </Space>

                    <Space>
                        <Radio.Group value={freq} onChange={(e) => setFreq(e.target.value)} size='small'>
                            <Radio.Button value="ME">Monthly</Radio.Button>
                            <Radio.Button value="QE">Quarterly</Radio.Button>
                            <Radio.Button value="YE">Yearly</Radio.Button>
                        </Radio.Group>
                    </Space>
                </Row>
            </Card>

            <Card bodyStyle={{ padding: '0.5rem' }} style={{ margin: '1rem 0rem', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Tabs 
                    activeKey={mode} 
                    onChange={handleTabChange} 
                    type="card" 
                    tabBarGutter={8}
                    tabBarStyle={{ marginBottom: '20px' }}
                >
                    {dataSource !== 'combined' && (
                        <>
                            <TabPane 
                                tab={<span style={{fontSize: '1.1em'}}>{dataSource === 'etf' ? '보유 잔고' : '보유 잔고'}</span>} 
                                key="holdings"
                            >
                                <div style={{ padding: '0 10px' }}>
                                    <Title level={4}>{getBreakdownTitle()}</Title>
                                    {loading ? <Loading /> : (
                                        <ReactECharts option={getBreakdownChartOption()} style={{ height: '500px' }} notMerge={true} />
                                    )}
                                </div>
                            </TabPane>

                            <TabPane 
                                tab={<span style={{fontSize: '1.1em'}}>{dataSource === 'etf' ? '순유입' : '순매입량'}</span>} 
                                key="net_change"
                            >
                                <div style={{ padding: '0 10px' }}>
                                    <Title level={4}>{getBreakdownTitle()}</Title>
                                    {loading ? <Loading /> : (
                                        <ReactECharts option={getBreakdownChartOption()} style={{ height: '500px' }} notMerge={true} />
                                    )}
                                </div>
                            </TabPane>

                            {dataSource === 'cb' && (
                                <TabPane tab={<span style={{fontSize: '1.1em'}}>순매입액</span>} key="value">
                                    <div style={{ padding: '0 10px' }}>
                                        <Title level={4}>Estimated Net Purchase Value ($B) vs Gold Price</Title>
                                        {loading ? <Loading /> : <ReactECharts option={getValueChartOption()} style={{ height: '600px' }} notMerge={true} />}
                                    </div>
                                </TabPane>
                            )}
                        </>
                    )}

                    {dataSource === 'combined' && (
                        <TabPane 
                            tab={<span style={{fontSize: '1.1em', color: '#d35400'}}><LineChartOutlined /> 합산 트랜드 </span>} 
                            key="composite"
                        >
                            <div style={{ padding: '0 10px' }}>
                                {loading ? <Loading /> : (
                                    <>
                                        <Title level={4}>금 ETF 순유입 & 중앙은행 금 순매입 누적 변화 (2004년 - 현재)</Title>
                                        <ReactECharts option={getCumulativeChartOption()} style={{ height: '500px' }} notMerge={true} />

                                        <Divider style={{ marginTop: '1rem', marginBottom: '1rem' }} />
                                        
                                        <Title level={4}>Aggregate Net Flows: ETF & 중앙은행</Title>
                                        <ReactECharts option={getComparisonChartOption()} style={{ height: '500px' }} notMerge={true} />
                                    </>
                                )}
                            </div>
                        </TabPane>
                    )}
                </Tabs>
            </Card>
        </div>
    );
};

export default GoldReservePage;